import dayjs from 'dayjs';
import { ToWords } from 'to-words';

const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';

/**
 * Generate the Collateral Summary document
 *
 * @param {Object=} data - The data to be used to generate the document
 */
export const generateCollateralSummary = (data = {}) => {
  const borderColor = '#9ca3af';
  const bigMargin = 20;

  const accountHolders = () => {
    if (!data.loan?.loanDistributions) return '';
    return data.loan?.loanDistributions
      .map(
        (d) =>
          `${d.person?.firstName} ${d.person?.middleName} ${d.person?.lastName}`
      )
      .join(', ');
  };
  const holders = accountHolders();
  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Collateral Summary', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'collateral summary, loan, finance', // keywords associated with the document
      author: data.company?.name, // company name
      subject: 'Collateral Summary', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        fontSize: 22,
        bold: true
      },
      { text: 'Statement of Collateral', fontSize: 14 },
      { text: "(Borrower's Information)", alignment: 'right' },
      { text: accountHolders(), alignment: 'right', bold: true },
      {
        text: `Account #: ${data.loan?.mainAccountNumber}`,
        alignment: 'right'
      },
      {
        text: `Branch: ${data.loan?.accreditedBranch?.branchName || ''}`,
        alignment: 'right'
      },
      {
        text: `Loan Product: ${data.loan?.loanProduct || ''}`,
        alignment: 'right'
      },

      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: bigMargin * 2,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*', '*', '*', '*'],
          headerRows: 1,

          body: [
            [
              { text: 'Owner', style: 'tableHeader' },
              { text: 'Type', style: 'tableHeader' },
              { text: 'Institution', style: 'tableHeader' },
              { text: 'Date Created', style: 'tableHeader' },
              { text: 'Pledged Amount', style: 'tableHeader' }
            ],
            [
              `ALFONSO ROBERT`,
              'Motor Vehicle',
              'N/A',
              dayjs().format('YYYY-MM-DD'),
              formatToCurrency(200_000)
            ],
            [
              { text: ' ', colSpan: 2 },
              ' ',
              { text: 'Total Collateral Pledged', bold: true, colSpan: 2 },
              ' ',
              {
                text: formatToCurrency(200_000),
                bold: true
              }
            ]
          ]
        }
      },
      {
        marginTop: bigMargin,
        marginBottom: bigMargin / 2,
        text: 'Declaration of Member',
        style: 'tableHeading',
        fontSize: 14
      },
      {
        text: "I hereby confirm that the information given herein is true in all respects, accurate and complete and that I have not withheld any information that might affect the Credit Union's decision. I am agreeable to the Credit Union taking such steps, as it may deem necessary to verify any of the information given."
      },
      { text: 'Signatures', marginTop: bigMargin, bold: true },
      // Map over the holders and display a signature line for each along with their name below
      holders &&
        holders.split(',').map((holder) => {
          return [
            {
              columns: [
                { text: '_______________________________', font: 'Roboto' },
                {
                  text: '_______________________________',
                  font: 'Roboto',
                  alignment: 'right'
                }
              ],
              marginTop: 15
            },
            {
              marginTop: 10,
              columns: [
                {
                  text: `Borrower: ${holder}`,
                  style: 'supSignature',
                  alignment: 'left'
                },
                {
                  text: 'Date Signed',
                  alignment: 'right',
                  style: 'supSignature'
                }
              ]
            }
          ];
        }),
      {
        marginTop: 15,
        columns: [
          { text: '_______________________________', font: 'Roboto' },
          {
            text: '_______________________________',
            font: 'Roboto',
            alignment: 'right'
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: `Prepared by: ${data.user?.username}`,
            style: 'supSignature',
            alignment: 'left'
          },
          { text: 'Date Signed', alignment: 'right', style: 'supSignature' }
        ]
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
